import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.ts';
import { formatDuration, formatDecimalHours, todayInPlant } from '../../shared/time.ts';
import { policz } from '../../shared/pl.ts';
import { Blad, Ladowanie, Pusto } from '../ui/index.tsx';

/**
 * Historia: rok → miesiąc → dzień → wpisy.
 *
 * Nie pokazujemy wszystkiego naraz. Miesiąc jest naturalnym okresem analizy
 * w produkcji, więc to on jest poziomem domyślnym; dni rozwijają się na żądanie,
 * a z dnia wchodzi się wprost do wpisów.
 */

interface Agregat {
  creditMin: number; availableMin: number; registeredMin: number; gapMin: number;
  efficiency: number | null; days: number;
}
interface Dzien { data: string; tydzien: number; total: Agregat; ludzi: number; wpisow: number }
interface Miesiac {
  miesiac: string; nazwa: string; total: Agregat;
  ludzi: number; wpisow: number; sztuk: number; dni: Dzien[];
}
interface Dane { rok: string; lata: string[]; miesiace: Miesiac[] }

const proc = (v: number | null) => (v === null ? '—' : `${Math.round(v * 100)}%`);
const klasa = (v: number | null) =>
  v === null ? 'text-plum-muted' : v >= 0.9 ? 'text-ok' : v >= 0.7 ? 'text-warn' : 'text-bad';

export default function Historia() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const rok = params.get('rok') ?? todayInPlant().slice(0, 4);

  const [d, setD] = useState<Dane | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  const [otwarty, setOtwarty] = useState<string | null>(null);

  const load = () => {
    setBlad(null);
    api.get<Dane>(`/historia?rok=${rok}`).then((r) => {
      setD(r);
      setOtwarty((m) => m ?? r.miesiace[0]?.miesiac ?? null);
    }).catch((e) => setBlad(e.message));
  };
  useEffect(load, [rok]);

  if (blad) return <Blad tresc={blad} ponow={load} />;
  if (!d) return <Ladowanie />;

  const rocznie = d.miesiace.reduce(
    (a, m) => ({
      credit: a.credit + m.total.creditMin,
      dostepne: a.dostepne + m.total.availableMin,
      wpisow: a.wpisow + m.wpisow,
      sztuk: a.sztuk + m.sztuk,
    }),
    { credit: 0, dostepne: 0, wpisow: 0, sztuk: 0 },
  );
  const efRoczna = rocznie.dostepne > 0 ? rocznie.credit / rocznie.dostepne : null;
  const najlepszy = [...d.miesiace].sort((a, b) => (b.total.efficiency ?? 0) - (a.total.efficiency ?? 0))[0];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-head-sm">Historia</h1>
        <div className="flex gap-1">
          {d.lata.map((r) => (
            <button key={r}
                    className={`subtab ${r === rok ? 'subtab-on' : ''}`}
                    onClick={() => { const n = new URLSearchParams(params); n.set('rok', r); setParams(n, { replace: true }); setOtwarty(null); }}>
              {r}
            </button>
          ))}
        </div>
      </div>

      {d.miesiace.length === 0 ? (
        <div className="card">
          <Pusto tytul={`Brak zarejestrowanej pracy w ${rok}`}
                 opis="Wybierz inny rok albo zarejestruj pierwszy wpis." />
        </div>
      ) : (
        <>
          <section className="card p-5 grid grid-cols-2 sm:grid-cols-4 gap-5">
            <div>
              <div className="text-label uppercase text-plum-muted mb-2">Efektywność roku</div>
              <div className={`kpi ${klasa(efRoczna)}`}>{proc(efRoczna)}</div>
              <div className="text-caption text-plum-muted mt-2">{policz(d.miesiace.length, 'miesiac')}</div>
            </div>
            <div>
              <div className="text-label uppercase text-plum-muted mb-2">Wypracowana norma</div>
              <div className="kpi">{formatDecimalHours(rocznie.credit, 0)}</div>
              <div className="text-caption text-plum-muted mt-2">z {formatDecimalHours(rocznie.dostepne, 0)} dostępnych</div>
            </div>
            <div>
              <div className="text-label uppercase text-plum-muted mb-2">Wpisy</div>
              <div className="kpi">{rocznie.wpisow}</div>
              <div className="text-caption text-plum-muted mt-2">{policz(rocznie.sztuk, 'sztuka')}</div>
            </div>
            <div>
              <div className="text-label uppercase text-plum-muted mb-2">Najlepszy miesiąc</div>
              <div className="kpi capitalize">{najlepszy?.nazwa ?? '—'}</div>
              <div className={`text-caption mt-2 ${klasa(najlepszy?.total.efficiency ?? null)}`}>
                {proc(najlepszy?.total.efficiency ?? null)}
              </div>
            </div>
          </section>

          <div className="space-y-3">
            {d.miesiace.map((m) => {
              const open = otwarty === m.miesiac;
              return (
                <section key={m.miesiac} className="card overflow-hidden">
                  <button
                    className="w-full px-5 py-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-left hover:bg-canvas transition-colors"
                    aria-expanded={open}
                    onClick={() => setOtwarty(open ? null : m.miesiac)}
                  >
                    <span className="font-semibold text-plum capitalize w-28">{m.nazwa}</span>
                    <span className="text-caption text-plum-muted">{policz(m.ludzi, 'pracownik')}</span>
                    <span className="text-caption text-plum-muted">{policz(m.wpisow, 'wpis')}</span>
                    <span className="text-caption text-plum-muted">{policz(m.sztuk, 'sztuka')}</span>
                    <span className="text-caption text-plum-muted tnum">
                      {formatDecimalHours(m.total.creditMin, 0)} normy
                    </span>
                    <span className="flex items-center gap-3 ml-auto">
                      <span className="hidden sm:block w-32 meter">
                        <span className="bg-plum" style={{ width: `${Math.min(100, (m.total.efficiency ?? 0) * 100)}%` }} />
                      </span>
                      <span className={`font-semibold tnum w-14 text-right ${klasa(m.total.efficiency)}`}>
                        {proc(m.total.efficiency)}
                      </span>
                      <span aria-hidden className="text-plum-muted">{open ? '▾' : '▸'}</span>
                    </span>
                  </button>

                  {open && (
                    <table className="tbl border-t border-line">
                      <thead>
                        <tr>
                          <th>Dzień</th>
                          <th className="text-right">Tydzień</th>
                          <th className="text-right">Pracowników</th>
                          <th className="text-right">Wpisów</th>
                          <th className="text-right">Rozpisane</th>
                          <th className="text-right">Norma</th>
                          <th className="text-right">Luka</th>
                          <th className="text-right">Efektywność</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {m.dni.map((dz) => (
                          <tr key={dz.data}>
                            <td className="tnum">{dz.data}</td>
                            <td className="text-right tnum text-plum-muted">{dz.tydzien}</td>
                            <td className="text-right tnum">{dz.ludzi}</td>
                            <td className="text-right tnum">{dz.wpisow}</td>
                            <td className="text-right tnum">{formatDuration(dz.total.registeredMin)}</td>
                            <td className="text-right tnum">{formatDuration(dz.total.creditMin)}</td>
                            <td className={`text-right tnum ${dz.total.gapMin > 60 ? 'text-bad' : 'text-plum-muted'}`}>
                              {formatDuration(Math.max(0, dz.total.gapMin))}
                            </td>
                            <td className={`text-right tnum font-semibold ${klasa(dz.total.efficiency)}`}>
                              {proc(dz.total.efficiency)}
                            </td>
                            <td className="text-right">
                              <button className="btn-quiet !min-h-0 !py-1 !px-2 text-caption"
                                      onClick={() => nav(`/praca?od=${dz.data}&do=${dz.data}`)}>
                                Wpisy →
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </section>
              );
            })}
          </div>
        </>
      )}

      <p className="text-caption text-plum-muted">
        Rok → miesiąc → dzień → wpisy. Każdy poziom da się rozwinąć i zejść do danych źródłowych,
        z których powstała liczba.
      </p>
    </div>
  );
}
